import { useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { ChalkMark } from './ChalkMark';
import { materialsChanged } from './seen';
import { ClassProvider, useClass } from './classContext';
import { LOILO_URL } from './classes';
import { Bookmark } from './Bookmark';
import { Icons } from './icons';
import { SlimeIcon } from './SlimeIcon';
import { useStrings } from './strings';
import { boardDate, todayISO } from './dates';
import { LiveNote, LiveProvider, useLive } from './live';
import './site.css';

const SECTIONS = ['board', 'schedule', 'materials', 'practice', 'play'] as const;

export function SiteShell() {
  return (
    <ClassProvider>
      <LiveProvider>
        <Shell />
      </LiveProvider>
    </ClassProvider>
  );
}

// The classes this device has joined, as name magnets on the rail: the one on show is cream, the others sit back.
function ClassSwitch() {
  const { board, sections, setClass } = useClass();
  const { s } = useStrings();
  const on = (section: string) => sections.length === 1 || board?.id === section;
  return (
    <div className="class-switch" role="group" aria-label={s.class}>
      {sections.map((p) => (
        <button
          key={p.section}
          className={on(p.section) ? 'btn' : 'btn quiet'}
          type="button"
          aria-pressed={on(p.section)}
          onClick={() => setClass(p.section)}
          translate="no"
        >
          {p.name}
        </button>
      ))}
      <Link className="btn quiet small add-class" to="/unlock?add=1" aria-label={s.manageClasses} title={s.manageClasses}>
        <span className="wide">{s.addClassShort}</span>
        <span className="narrow">+</span>
      </Link>
    </div>
  );
}

// On a phone the class is one magnet in the top bar; pressing it opens the others and the add-or-remove link.
function ClassPicker() {
  const { board, sections, setClass } = useClass();
  const { s } = useStrings();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const current = sections.find((p) => p.section === board?.id) ?? sections[0];
  return (
    <div className="class-pick" ref={ref}>
      <button className="btn" type="button" aria-expanded={open} aria-label={`${s.class}: ${current?.name ?? ''}`} onClick={() => setOpen((o) => !o)}>
        <span translate="no">{current?.name}</span>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="class-pop">
          {sections.map((p) => (
            <button
              key={p.section}
              className={board?.id === p.section ? 'btn' : 'btn quiet'}
              type="button"
              aria-pressed={board?.id === p.section}
              onClick={() => {
                setClass(p.section);
                setOpen(false);
              }}
              translate="no"
            >
              {p.name}
            </button>
          ))}
          <Link className="add-class" to="/unlock?add=1">
            + {s.manageClasses}
          </Link>
        </div>
      )}
    </div>
  );
}

// A section link: the current one is underlined in yellow chalk, a hover draws a white one, a dot means it changed.
function SectionLink({ section, changed, live }: { section: (typeof SECTIONS)[number]; changed: boolean; live?: boolean }) {
  const { s } = useStrings();
  const [hover, setHover] = useState(0);
  // (the Slime tab opens the slime's page: its drawing, and where it can go from there)
  return (
    <NavLink to={`/${section}`} onMouseEnter={() => setHover((n) => n + 1)} onMouseLeave={() => setHover(0)}>
      {({ isActive }) => (
        <>
          {section === 'play' ? (
            // your own slime; while a class game is open it gets a chalk ring
            <span className="mark-host icon-host">
              <SlimeIcon />
              {live && <ChalkMark kind="ring" pad={4} seed={9} dur={0.5} width={2} />}
            </span>
          ) : (
            Icons[section]
          )}
          <span className="label">
            {s[section]}
            {isActive ? (
              <ChalkMark kind="underline" color="yellow" pad={6} seed={section.length} dur={0.45} />
            ) : (
              hover > 0 && <ChalkMark kind="underline" pad={6} seed={section.length + 9} dur={0.3} replay={hover} alpha={0.7} />
            )}
          </span>
          {changed && !isActive && (
            <span className="new-dot" role="img" aria-label="new">
              <ChalkMark kind="dot" pad={0} seed={3} dur={0.3} />
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function Shell() {
  const { s, toggle } = useStrings();
  const { board, status } = useClass();
  const live = useLive();
  const onPlay = useLocation().pathname.startsWith('/play'); // re-checks the dots on every move too
  const changed = board ? materialsChanged(board) : false;
  // a class made on the host screen has no board yet: its students get the Slime tab alone, and land there
  const slimeOnly = !!board?.slimeOnly;
  const links = (slimeOnly ? (['play'] as const) : SECTIONS).map((k) => <SectionLink key={k} section={k} changed={k === 'materials' && changed} live={k === 'play' && !!live} />);
  if (slimeOnly && !onPlay) return <Navigate to="/play" replace />;
  return (
    // while the note is up its Join is the page's one yellow
    <div className={live ? 'site has-live' : 'site'}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <div className="site-shell">
        <aside className="rail">
          <Link className="brand" to="/board" translate="no" aria-label="GakuShiori">
            <Bookmark size={22} />
            <span className="wide">GakuShiori</span>
          </Link>
          <ClassSwitch />
          <nav className="nav" aria-label="Sections">
            {links}
          </nav>
          <div className="rail-foot">
            <a className="btn quiet small" href={LOILO_URL} target="_blank" rel="noreferrer">
              {s.loiloShort} {Icons.out}
            </a>
            <button className="btn quiet small" type="button" onClick={toggle} aria-label={s.languageLabel}>
              <span className="wide">{s.language}</span>
              <span className="narrow">{s.language === 'English' ? 'EN' : 'JA'}</span>
            </button>
          </div>
        </aside>
        <header className="topbar">
          <Link className="brand" to="/board" aria-label="GakuShiori">
            <Bookmark size={22} />
          </Link>
          <ClassPicker />
          <p className="board-date" lang="ja">
            {boardDate(todayISO())}
          </p>
          <button className="btn quiet small lang" type="button" onClick={toggle} aria-label={s.languageLabel}>
            {s.language === 'English' ? 'EN' : 'JA'}
          </button>
        </header>
        <main className="site-main" id="main">
          {status === 'offline' && board && !onPlay && <p className="offline-note">{s.offlineNote}</p>}
          <LiveNote />
          {/* Play is the slime's page: it needs no board, so it opens on the train with no signal too */}
          {board || onPlay ? <Outlet /> : <p className="dim">{status === 'offline' ? s.offlineNoBoard : s.loading}</p>}
          <footer className="site-foot" translate="no">
            © 2026 GakuShiori. All rights reserved. · <Link to="/privacy">{s.privacy}</Link>
            {onPlay && (
              <>
                {' · '}
                <Link to="/play/host">{s.forTeachers}</Link>
              </>
            )}
          </footer>
        </main>
        <nav className="tabbar" aria-label="Sections">
          {links}
        </nav>
      </div>
    </div>
  );
}
